<template>
  <div class="membership-card">
    <div class="membership-card__header">
      <div>
        <span class="membership-card__eyebrow">Тариф</span>
        <h2>{{ title }}</h2>
      </div>
      <span class="membership-card__status" :class="{ active: hasAccess }">
        {{ statusText }}
      </span>
    </div>

    <div v-if="isLoading" class="loader my-4"></div>

    <template v-else>
      <div v-if="isAdmin" class="membership-card__notice">
        <strong>Администраторский доступ</strong>
        <span>Доступ ко всем данным включен постоянно и не ограничен датой окончания.</span>
      </div>

      <div v-else-if="activeMembership" class="membership-card__summary">
        <div>
          <span>Активный тариф</span>
          <strong>{{ activeMembership.plan_name || currentUser.name_membership || 'Оплаченный тариф' }}</strong>
        </div>
        <div>
          <span>Действует до</span>
          <strong>{{ formatDate(activeMembership.end_date || currentUser.membership_end_date) }}</strong>
        </div>
        <div>
          <span>Осталось</span>
          <strong>{{ activeMembership.left_days }} дн.</strong>
        </div>
      </div>

      <div v-else-if="isFreeCompanyAccessActive" class="membership-card__notice">
        <strong>Промо-доступ до 01.09.2026</strong>
        <span>Данные компаний временно открыты бесплатно. С 1 сентября доступ снова будет по тарифу.</span>
      </div>

      <div v-else class="membership-card__notice muted">
        <strong>Активного тарифа нет</strong>
        <span>Информация по компаниям закрыта до оплаты тарифа.</span>
      </div>

      <div v-if="!isAdmin" class="membership-card__actions">
        <div class="membership-card__actions-title">
          {{ actionsTitle }}
        </div>

        <div v-if="plans.length" class="membership-card__plans">
          <button
            v-for="plan in plans"
            :key="plan.id"
            class="membership-card__plan"
            :disabled="pendingPlanId === plan.id"
            @click="startPayment(plan.id)"
          >
            <span>
              <strong>{{ plan.name }}</strong>
              <small>{{ planPeriod(plan.type) }}</small>
            </span>
            <b>{{ formatPrice(plan.price) }} ₽</b>
            <em v-if="pendingPlanId === plan.id">Создаём заказ...</em>
            <em v-else>{{ planActionText(plan.id) }}</em>
          </button>
        </div>

        <button v-else class="black-button" @click="goToPricing">Перейти к тарифам</button>

        <p v-if="errorMessage" class="membership-card__error">{{ errorMessage }}</p>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';

  export default defineComponent({
    name: 'MembershipCard',
    setup() {
      const freeCompanyAccessUntil = '2026-09-01T00:00:00+03:00';
      const store = useStore();
      const router = useRouter();
      const memberships = ref([]);
      const plans = ref([]);
      const isLoading = ref(true);
      const pendingPlanId = ref(null);
      const errorMessage = ref('');

      const currentUser = computed(() => store.state.user.user || {});
      const isAdmin = computed(() => Boolean(currentUser.value.is_staff || currentUser.value.is_superuser));
      const isFreeCompanyAccessActive = computed(() => new Date() < new Date(freeCompanyAccessUntil));

      const isFutureOrToday = (date) => {
        if (!date) {
          return false;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const parsed = new Date(`${date}T00:00:00`);
        return parsed >= today;
      };

      const activeMembership = computed(() => {
        return [...memberships.value]
          .filter((membership) => membership.is_active || isFutureOrToday(membership.end_date))
          .sort((a, b) => new Date(b.end_date || 0) - new Date(a.end_date || 0))[0];
      });

      const hasAccess = computed(() =>
        Boolean(
          isAdmin.value ||
            isFreeCompanyAccessActive.value ||
            currentUser.value.membership_exists ||
            activeMembership.value
        )
      );
      const title = computed(() => {
        if (isAdmin.value) {
          return 'Доступ оплачен';
        }
        if (activeMembership.value) {
          return activeMembership.value.plan_name || currentUser.value.name_membership || 'Активный тариф';
        }
        if (isFreeCompanyAccessActive.value) {
          return 'Промо-доступ';
        }
        return 'Тариф не подключен';
      });
      const statusText = computed(() => {
        if (isAdmin.value || activeMembership.value) {
          return 'Оплачен';
        }
        if (isFreeCompanyAccessActive.value) {
          return 'Бесплатно';
        }
        return 'Не оплачен';
      });
      const actionsTitle = computed(() => {
        if (activeMembership.value) {
          return 'Продлить или выбрать другой тариф';
        }
        if (isFreeCompanyAccessActive.value) {
          return 'Можно оформить тариф заранее';
        }
        return 'Выберите тариф для оплаты';
      });

      const formatDate = (date) => {
        if (!date) {
          return 'Без срока';
        }
        return new Intl.DateTimeFormat('ru-RU').format(new Date(`${date}T00:00:00`));
      };

      const formatPrice = (price) => {
        return new Intl.NumberFormat('ru-RU').format(price || 0);
      };

      const planPeriod = (type) => {
        return type === 'yearly' ? 'ежегодно' : 'ежемесячно';
      };

      const planActionText = (planId) => {
        if (activeMembership.value && activeMembership.value.plan === planId) {
          return 'Продлить';
        }
        if (isFreeCompanyAccessActive.value) {
          return 'Оформить';
        }
        return 'Оплатить';
      };

      const goToPricing = () => {
        router.push({ path: '/', hash: '#pricing-section' });
      };

      const startPayment = async (planId) => {
        errorMessage.value = '';
        pendingPlanId.value = planId;
        try {
          const res = await store.dispatch('payments/createOrder', planId);
          window.location.href = res.order_url;
        } catch (_error) {
          errorMessage.value = 'Не удалось создать заказ. Попробуйте ещё раз.';
          pendingPlanId.value = null;
        }
      };

      onMounted(async () => {
        isLoading.value = true;
        try {
          await store.dispatch('user/me');
        } catch (_error) {
          // Данные пользователя уже есть в localStorage, если обновление не удалось.
        }

        if (!isAdmin.value) {
          try {
            const [membershipList, planList] = await Promise.all([
              store.dispatch('payments/getMemberships'),
              store.dispatch('payments/getPlans'),
            ]);
            memberships.value = membershipList || [];
            plans.value = [...(planList || [])].sort((a, b) => a.price - b.price);
          } catch (_error) {
            errorMessage.value = 'Не удалось загрузить тарифы.';
          }
        }

        isLoading.value = false;
      });

      return {
        currentUser,
        isAdmin,
        isFreeCompanyAccessActive,
        hasAccess,
        activeMembership,
        plans,
        isLoading,
        pendingPlanId,
        errorMessage,
        title,
        statusText,
        actionsTitle,
        formatDate,
        formatPrice,
        planPeriod,
        planActionText,
        goToPricing,
        startPayment,
      };
    },
  });
</script>
